import { Button, ConfirmProvider, EmptyState, LoadingState } from '@platform/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
    Link,
    Outlet,
    RouterProvider,
    createRootRoute,
    createRoute,
    createRouter,
    lazyRouteComponent,
    useNavigate,
    useRouter,
    useRouterState,
    type ErrorComponentProps,
} from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';

import { useEffect } from 'react';
import './app.css';
import { AppShell } from './components/AppShell';
import { I18nProvider, useMessages } from './i18n';
import { ApiError } from './lib/api';
import { validateSubscriptionSearch } from './lib/billing';
import { validateChannelSearch } from './lib/channels';
import { validateProfitabilitySearch, validateSalesTrendSearch } from './lib/finance';
import { validateMappingSearch } from './lib/mapping';
import { validateOrderSearch } from './lib/order-page';
import { usePageTitle } from './lib/page-title';
import { validateProductSearch } from './lib/product-page';
import { SessionProvider, useSession, type Permission } from './lib/session';
import { DashboardPage } from './pages/DashboardPage';
import { SignInPage } from './pages/SignInPage';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // An expired session and a validation failure are answers, not outages;
            // retrying them wastes time and hides the real message.
            retry: (failureCount, error) => !(error instanceof ApiError && error.status < 500) && failureCount < 2,
            refetchOnWindowFocus: false,
            staleTime: 10_000,
        },
    },
});

// The way in for someone with no session. Being here also means a signed-in
// person is sent away: there is nothing for them on a sign-in form.
const PUBLIC_PATHS = new Set(['/giris', '/kayit', '/davet']);

// The pages somebody reaches from their inbox.
//
// Neither set above works for these. They cannot require a session — the link
// is read wherever the mail is, quite possibly on a device that has never
// signed in, and a password reset is the only way back into an account
// somebody is locked out of. But they must not turn a signed-in person away
// either: following a verification link while already signed in is the
// ordinary case, and bouncing them to the dashboard silently swallows the
// very thing they clicked.
const LINK_PATHS = new Set(['/e-posta-dogrula', '/e-posta-degistir', '/parola-unuttum', '/parola-sifirla']);

// Where a person accepts the current terms for the company they chose, before
// the panel goes on (ADR 0018 §1).
const TERMS_PATH = '/kullanim-kosullari';

/**
 * Decides what a given session may see.
 *
 * Three states, in order: signed out, signed in without a company selected, and
 * fully ready. Routing on anything other than the server's own answer would let
 * the panel disagree with the API about who is signed in.
 */
function AuthGate({ children }: { children: ReactNode }) {
    const loadingLabel = useMessages().common.loading;
    const { me, isLoading, isReady } = useSession();
    const navigate = useNavigate();
    const pathname = useRouterState({
        select: (state) => state.location.pathname,
    });
    const isPublic = PUBLIC_PATHS.has(pathname);
    const isLink = LINK_PATHS.has(pathname);
    const termsPending = Boolean(me && isReady && me.terms?.pending);

    useEffect(() => {
        if (isLoading) return;
        // A link page decides nothing: it is reached with or without a session.
        if (isLink) return;

        if (!me && !isPublic) {
            navigate({ to: '/giris', replace: true });
            return;
        }
        if (me && isPublic) {
            navigate({ to: '/', replace: true });
            return;
        }
        if (me && !isReady && pathname !== '/firma-sec') {
            navigate({ to: '/firma-sec', replace: true });
            return;
        }
        // The current terms before anything else in the company (ADR 0018 §1);
        // once accepted, the page has nothing more to say.
        if (termsPending && pathname !== TERMS_PATH) {
            navigate({ to: TERMS_PATH, replace: true });
            return;
        }
        if (me && isReady && !termsPending && pathname === TERMS_PATH) {
            navigate({ to: '/', replace: true });
        }
    }, [isLoading, me, isReady, termsPending, isPublic, isLink, pathname, navigate]);

    // While the terms wait, nothing behind them is mounted: no page of the
    // company draws, and none of its requests leave to be refused.
    if (isLoading || (termsPending && pathname !== TERMS_PATH && !isLink)) {
        return <LoadingState label={loadingLabel} className="min-h-dvh" />;
    }
    return <>{children}</>;
}

/** One confirmation dialog for the whole panel, worded in the seller's language. */
function Confirmations({ children }: { children: ReactNode }) {
    const t = useMessages();
    return (
        <ConfirmProvider
            labels={{
                confirm: t.common.confirm,
                cancel: t.common.cancel,
                close: t.common.close,
            }}
        >
            {children}
        </ConfirmProvider>
    );
}

const rootRoute = createRootRoute({
    component: () => (
        <QueryClientProvider client={queryClient}>
            <SessionProvider>
                <Confirmations>
                    <AuthGate>
                        <Outlet />
                    </AuthGate>
                </Confirmations>
            </SessionProvider>
        </QueryClientProvider>
    ),
});

const signInRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/giris',
    component: SignInPage,
});

const acceptInviteRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/davet',
    component: lazyRouteComponent(() => import('./pages/AcceptInvitePage'), 'AcceptInvitePage'),
});

const verifyEmailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/e-posta-dogrula',
    component: lazyRouteComponent(() => import('./pages/VerifyEmailPage'), 'VerifyEmailPage'),
});

// The three pages a person reaches from their inbox, or when they cannot get
// in at all. All outside the shell: whoever is here has no session, which is
// the reason they are here.
const forgotPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/parola-unuttum',
    component: lazyRouteComponent(() => import('./pages/ForgotPasswordPage'), 'ForgotPasswordPage'),
});

const resetPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/parola-sifirla',
    component: lazyRouteComponent(() => import('./pages/ResetPasswordPage'), 'ResetPasswordPage'),
});

const confirmEmailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/e-posta-degistir',
    component: lazyRouteComponent(() => import('./pages/ConfirmEmailPage'), 'ConfirmEmailPage'),
});

// Where a support hand-off from the platform panel lands. Outside the shell
// because the operator arriving has no session here yet — that is what the
// token in the URL is for.
const supportSessionRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/destek-girisi',
    component: lazyRouteComponent(() => import('./pages/SupportSessionPage'), 'SupportSessionPage'),
});

const signUpRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/kayit',
    component: lazyRouteComponent(() => import('./pages/SignUpPage'), 'SignUpPage'),
});

// Outside the shell, as choosing a company is: nothing of the company is
// shown until its terms are accepted.
const termsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: TERMS_PATH,
    component: lazyRouteComponent(() => import('./pages/TermsPage'), 'TermsPage'),
});

const selectCompanyRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/firma-sec',
    component: lazyRouteComponent(() => import('./pages/SelectCompanyPage'), 'SelectCompanyPage'),
});

// Authenticated pages hang off a shared shell so navigation and the topbar are
// mounted once rather than re-rendered per page. Each page names the permission
// it needs as its route's static data, the one place that says so: the menu and
// the section tabs read it, and the shell refuses a page the session lacks it
// for (docs/11 §8). The permission is the one the API asks of the page's own
// data, or of its one action where the page exists for that action.
const shellRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'shell',
    component: AppShell,
});

const dashboardRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/',
    component: DashboardPage,
});

const productsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/urunler',
    staticData: { permission: 'product.read' },
    component: lazyRouteComponent(() => import('./pages/ProductsPage'), 'ProductsPage'),
});

// Declared before the parameterised route so "/urunler/yeni" is not read as a
// product whose id happens to be "yeni".
const newProductRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/urunler/yeni',
    staticData: { permission: 'product.write' },
    component: lazyRouteComponent(() => import('./pages/NewProductPage'), 'NewProductPage'),
});

const productImportRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/urunler/ice-aktar',
    staticData: { permission: 'product.write' },
    component: lazyRouteComponent(() => import('./pages/ProductImportPage'), 'ProductImportPage'),
});

const bulkUpdateRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/toplu-guncelleme',
    staticData: { permission: 'price.write' },
    component: lazyRouteComponent(() => import('./pages/BulkUpdatePage'), 'BulkUpdatePage'),
});

const productDetailRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/urunler/$productId',
    staticData: { permission: 'product.read' },
    validateSearch: validateProductSearch,
    component: lazyRouteComponent(() => import('./pages/ProductDetailPage'), 'ProductDetailPage'),
});

const ordersRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/siparisler',
    staticData: { permission: 'order.read' },
    component: lazyRouteComponent(() => import('./pages/OrdersPage'), 'OrdersPage'),
});

const orderDetailRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/siparisler/$orderId',
    staticData: { permission: 'order.read' },
    validateSearch: validateOrderSearch,
    component: lazyRouteComponent(() => import('./pages/OrderDetailPage'), 'OrderDetailPage'),
});

const teamRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/ekip',
    staticData: { permission: 'user.read' },
    component: lazyRouteComponent(() => import('./pages/TeamPage'), 'TeamPage'),
});

const storefrontRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/magaza',
    staticData: { permission: 'storefront.read' },
    component: lazyRouteComponent(() => import('./pages/StorefrontPage'), 'StorefrontPage'),
});

const shippingRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/kargo',
    staticData: { permission: 'order.read' },
    component: lazyRouteComponent(() => import('./pages/ShippingPage'), 'ShippingPage'),
});

const claimsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/iadeler',
    staticData: { permission: 'order.read' },
    component: lazyRouteComponent(() => import('./pages/ClaimsPage'), 'ClaimsPage'),
});

const mappingRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/kategori-eslestirme',
    staticData: { permission: 'listing.read' },
    validateSearch: validateMappingSearch,
    component: lazyRouteComponent(() => import('./pages/MappingPage'), 'MappingPage'),
});

// Product identity: the queue of channel products the importer could not tell
// apart from ones the seller already has.
const productMatchesRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/urun-eslestirme',
    staticData: { permission: 'product.read' },
    component: lazyRouteComponent(() => import('./pages/ProductMatchesPage'), 'ProductMatchesPage'),
});

// The company's own brands, managed in one place for the panel and the shop
// (ADR 0022 §12).
const brandsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/markalar',
    staticData: { permission: 'product.read' },
    component: lazyRouteComponent(() => import('./pages/BrandsPage'), 'BrandsPage'),
});

// Brands: the company's against each marketplace's list (ADR 0022 §9).
const brandMappingRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/marka-eslestirme',
    staticData: { permission: 'listing.read' },
    validateSearch: validateMappingSearch,
    component: lazyRouteComponent(() => import('./pages/BrandMappingPage'), 'BrandMappingPage'),
});

const financeRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/finans',
    staticData: { permission: 'report.read' },
    component: lazyRouteComponent(() => import('./pages/FinancePage'), 'FinancePage'),
});

// A sibling of /finans rather than a page under it: the section's tabs mark a
// route and every page under it, so /finans/karlilik would light both.
const profitabilityRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/karlilik',
    staticData: { permission: 'report.read' },
    validateSearch: validateProfitabilitySearch,
    component: lazyRouteComponent(() => import('./pages/ProfitabilityPage'), 'ProfitabilityPage'),
});

// Which products bring in the revenue and which held stock does not sell
// (EPZ-0227): Finans's third tab, a sibling for the same reason.
const productPerformanceRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/urun-performansi',
    staticData: { permission: 'report.read' },
    component: lazyRouteComponent(() => import('./pages/ProductPerformancePage'), 'ProductPerformancePage'),
});

// A tab of Finans, at its own address beside it (docs/11 §8): ?urun= narrows it
// to the product whose page opened it.
const salesTrendRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/satis-trendi',
    staticData: { permission: 'report.read' },
    validateSearch: validateSalesTrendSearch,
    component: lazyRouteComponent(() => import('./pages/SalesTrendPage'), 'SalesTrendPage'),
});

const warehousesRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/depolar',
    staticData: { permission: 'inventory.read' },
    component: lazyRouteComponent(() => import('./pages/WarehousesPage'), 'WarehousesPage'),
});

const pricingRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/fiyatlandirma',
    staticData: { permission: 'price_rule.read' },
    component: lazyRouteComponent(() => import('./pages/PricingPage'), 'PricingPage'),
});

const suppliersRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/tedarikciler',
    staticData: { permission: 'feed.read' },
    component: lazyRouteComponent(() => import('./pages/SuppliersPage'), 'SuppliersPage'),
});

const feedDetailRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/tedarikciler/$feedId',
    staticData: { permission: 'feed.read' },
    component: lazyRouteComponent(() => import('./pages/FeedDetailPage'), 'FeedDetailPage'),
});

const customersRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/musteriler',
    staticData: { permission: 'order.read' },
    component: lazyRouteComponent(() => import('./pages/CustomersPage'), 'CustomersPage'),
});

const customerDetailRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/musteriler/$customerId',
    staticData: { permission: 'order.read' },
    component: lazyRouteComponent(() => import('./pages/CustomerDetailPage'), 'CustomerDetailPage'),
});

const stockRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/stok',
    staticData: { permission: 'inventory.read' },
    component: lazyRouteComponent(() => import('./pages/StockPage'), 'StockPage'),
});

const channelsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/kanallar',
    staticData: { permission: 'channel.read' },
    component: lazyRouteComponent(() => import('./pages/ChannelsPage'), 'ChannelsPage'),
});

const channelDetailRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/kanallar/$accountId',
    staticData: { permission: 'channel.read' },
    validateSearch: validateChannelSearch,
    component: lazyRouteComponent(() => import('./pages/ChannelDetailPage'), 'ChannelDetailPage'),
});

const subscriptionRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/abonelik',
    staticData: { permission: 'billing.read' },
    validateSearch: validateSubscriptionSearch,
    component: lazyRouteComponent(() => import('./pages/SubscriptionPage'), 'SubscriptionPage'),
});

const supportRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/destek',
    staticData: { permission: 'order.read' },
    component: lazyRouteComponent(() => import('./pages/SupportPage'), 'SupportPage'),
});

const questionsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/sorular',
    staticData: { permission: 'order.read' },
    component: lazyRouteComponent(() => import('./pages/QuestionsPage'), 'QuestionsPage'),
});

const webhooksRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/webhooks',
    staticData: { permission: 'webhook.read' },
    component: lazyRouteComponent(() => import('./pages/WebhooksPage'), 'WebhooksPage'),
});

const apiTokensRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/api-erisimi',
    staticData: { permission: 'api_token.read' },
    component: lazyRouteComponent(() => import('./pages/ApiTokensPage'), 'ApiTokensPage'),
});

const notificationsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/bildirimler',
    component: lazyRouteComponent(() => import('./pages/NotificationsPage'), 'NotificationsPage'),
});

const couponsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/kuponlar',
    staticData: { permission: 'storefront.read' },
    component: lazyRouteComponent(() => import('./pages/CouponsPage'), 'CouponsPage'),
});

const promotionsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/otomatik-indirimler',
    staticData: { permission: 'storefront.read' },
    component: lazyRouteComponent(() => import('./pages/PromotionsPage'), 'PromotionsPage'),
});

const sizeGuidesRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/beden-tablolari',
    staticData: { permission: 'storefront.read' },
    component: lazyRouteComponent(() => import('./pages/SizeGuidesPage'), 'SizeGuidesPage'),
});

const accountRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/hesabim',
    component: lazyRouteComponent(() => import('./pages/AccountPage'), 'AccountPage'),
});

const newsletterRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/bulten',
    staticData: { permission: 'storefront.read' },
    component: lazyRouteComponent(() => import('./pages/NewsletterPage'), 'NewsletterPage'),
});

const reviewsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/yorumlar',
    staticData: { permission: 'storefront.read' },
    component: lazyRouteComponent(() => import('./pages/ReviewsPage'), 'ReviewsPage'),
});

const invoicesRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/faturalar',
    staticData: { permission: 'invoice.read' },
    component: lazyRouteComponent(() => import('./pages/InvoicesPage'), 'InvoicesPage'),
});

// Declared before the parameterised route so "/faturalar/ayarlar" is not read
// as an invoice whose id happens to be "ayarlar".
const invoiceSettingsRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/faturalar/ayarlar',
    staticData: { permission: 'invoice.read' },
    component: lazyRouteComponent(() => import('./pages/InvoiceSettingsPage'), 'InvoiceSettingsPage'),
});

const invoiceDetailRoute = createRoute({
    getParentRoute: () => shellRoute,
    path: '/faturalar/$invoiceId',
    staticData: { permission: 'invoice.read' },
    component: lazyRouteComponent(() => import('./pages/InvoiceDetailPage'), 'InvoiceDetailPage'),
});

const routeTree = rootRoute.addChildren([
    signInRoute,
    signUpRoute,
    acceptInviteRoute,
    verifyEmailRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    confirmEmailRoute,
    supportSessionRoute,
    selectCompanyRoute,
    termsRoute,
    shellRoute.addChildren([
        dashboardRoute,
        productsRoute,
        newProductRoute,
        productImportRoute,
        bulkUpdateRoute,
        productDetailRoute,
        channelsRoute,
        channelDetailRoute,
        ordersRoute,
        orderDetailRoute,
        stockRoute,
        shippingRoute,
        claimsRoute,
        mappingRoute,
        productMatchesRoute,
        brandsRoute,
        brandMappingRoute,
        financeRoute,
        profitabilityRoute,
        productPerformanceRoute,
        salesTrendRoute,
        customersRoute,
        customerDetailRoute,
        suppliersRoute,
        feedDetailRoute,
        pricingRoute,
        warehousesRoute,
        storefrontRoute,
        subscriptionRoute,
        supportRoute,
        questionsRoute,
        webhooksRoute,
        apiTokensRoute,
        notificationsRoute,
        couponsRoute,
        sizeGuidesRoute,
        promotionsRoute,
        reviewsRoute,
        newsletterRoute,
        accountRoute,
        teamRoute,
        invoicesRoute,
        invoiceSettingsRoute,
        invoiceDetailRoute,
    ]),
]);

/** An address that matches no page: say so, and give the way back. */
function NotFound() {
    const t = useMessages();
    usePageTitle(t.common.notFound.title);
    return (
        <div className="grid min-h-dvh place-items-center">
            <EmptyState
                titleAs="h1"
                title={t.common.notFound.title}
                description={t.common.notFound.body}
                action={
                    <Button asChild variant="secondary">
                        <Link to="/">{t.common.notFound.home}</Link>
                    </Button>
                }
            />
        </div>
    );
}

/**
 * What the browsers say when a page's file did not arrive: the three messages
 * the router itself reloads on once, a deploy having replaced the file.
 */
const CHUNK_FAILURE = /(Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed)/;

/**
 * Stands in while a page's code is fetched (ADR 0004 §1). The router shows it
 * only after its pending delay, so a fast load does not flash.
 */
function PagePending() {
    const t = useMessages();
    return <LoadingState label={t.common.loading} />;
}

/**
 * Stands in for a page that failed to draw (docs/11 §8). It words the failure and
 * never shows the error — the console has the message, and a seller can do
 * nothing with it.
 */
function PageError({ error, reset }: ErrorComponentProps) {
    const t = useMessages();
    const router = useRouter();
    usePageTitle(t.common.error.title);

    function retry() {
        // The router keeps a page's failed import and throws it again on every
        // draw, so drawing the route once more cannot fetch the file; only a
        // reload does (ADR 0004 §1).
        if (CHUNK_FAILURE.test(String(error))) {
            window.location.reload();
            return;
        }
        // The cache may hold the very answer the page could not draw, and a page
        // mounted over it fails the same way before it can ask again. Dropping
        // what no screen is watching makes the retry ask afresh.
        queryClient.removeQueries({ type: 'inactive' });
        void router.invalidate();
        reset();
    }

    return (
        <EmptyState
            titleAs="h1"
            icon={<TriangleAlert aria-hidden="true" />}
            title={t.common.error.title}
            description={t.common.error.body}
            action={
                <>
                    <Button onClick={retry}>{t.common.error.retry}</Button>
                    <Button asChild variant="secondary">
                        <Link to="/">{t.common.error.home}</Link>
                    </Button>
                </>
            }
        />
    );
}

const router = createRouter({
    routeTree,
    defaultPreload: 'intent',
    defaultPendingComponent: PagePending,
    defaultNotFoundComponent: NotFound,
    defaultErrorComponent: PageError,
});

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
    interface StaticDataRouteOption {
        /** The permission a page needs; a page without one is open to every signed-in person. */
        permission?: Permission;
    }
}

const container = document.getElementById('root');
if (!container) {
    throw new Error('#root is missing from index.html');
}

// The dictionary wraps the router rather than sitting in the root route, so the
// error screen can word itself even when the root's own providers are what
// failed (docs/11 §8).
createRoot(container).render(
    <StrictMode>
        <I18nProvider>
            <RouterProvider router={router} />
        </I18nProvider>
    </StrictMode>,
);
